import {
  IRequirementResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import {
  WSAlertProps,
  WSDataItemProps,
  WSPageToolbarAction,
  WSPillProps,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { RequirementEvent } from "./utils";
import { CreateRequirementDefinitionSharedFilePartialForm } from "./modules/SharedFile/types";
import { CreateRequirementDefinitionSignaturePartialForm } from "./modules/Signature/types";
import { CreateRequirementDefinitionBackgroundChecksPartialForm } from "./modules/BackgroundChecks/types";
import { DataViewProps } from "../../shared/components/DataView";
import { IBackgroundCheckRequestResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";
import { ISharedFileRequestResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { ISignedDocumentResponse } from "@wingspanhq/signed-documents/dist/lib/interfaces";
import { RequirementReviewStrategy } from "@wingspanhq/payments/dist/interfaces/requirement";
import { StepItem } from "../../shared/components/Steps";
import { IIdentityVerificationRequestResponse } from "@wingspanhq/users";
import { CreateRequirementDefinitionInsuranceCoveragePartialForm } from "../RequirementDefinitions/modules/InsuranceCoverage/createRequirementDefinition/types";
import { InsuranceCoverageMonitor } from "../../services/api/insurance/monitor/types";

export enum RequirementContextType {
  Payer = "Payer",
  Payee = "Payee"
}

export type RequirementTableItemData = IRequirementResponse;

export type RequirementAdditionalData =
  | IBackgroundCheckRequestResponse
  | ISharedFileRequestResponse
  | ISignedDocumentResponse
  | IIdentityVerificationRequestResponse
  | InsuranceCoverageMonitor
  | undefined;

type DataItem<T extends RequirementType> = IRequirementResponse;

export type ContextItem = {
  // payerId?: string;
  // payeeId?: string;
  // engagementsLength?: number;
  showOverrideActions?: boolean;
};

export type TableCellProps = React.ComponentProps<typeof WSTableCell>;

export type RenderCellFunction<T extends RequirementType> = (
  dataItem: DataItem<T>,
  context: ContextItem
) => TableCellProps;

export type CellsByContext<T extends RequirementType> = {
  [keyof in RequirementContextType]: RenderCellFunction<T>;
};

export type RequirementAction = WSPageToolbarAction & {
  noTable?: boolean;
  noOnboarding?: boolean;
  mainAction?: boolean;
};

export type RequirementFieldsConfig<T extends RequirementType> = {
  requirementDetailsWidgets?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => React.ReactNode;
  };
  isActionRequired?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => boolean;
  };
  requirementDetailsProgressWidget?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => StepItem[] | undefined;
  };
  requirementInstructionsWidgets?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => React.ReactNode;
  };
  requirementInstructions?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => (instructions: string) => React.ReactNode;
  };
  requirementName?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => string;
  };
  toolbarActions?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => RequirementAction[];
  };
  requirementStatusAlert?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => WSAlertProps | undefined;
  };
  requirementBasicInformation?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => WSDataItemProps[];
  };
  requirementDetails?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => DataViewProps | React.ReactNode;
  };
  requirementAdditionalContent?: {
    [keyof in RequirementContextType]: (
      dataItem: DataItem<T>,
      contextItem: ContextItem
    ) => React.ReactNode;
  };
  nameCell: CellsByContext<T>;
  typeCell: CellsByContext<T>;
  statusCell: CellsByContext<T>;
  getAdditionalData(
    dataItem: DataItem<T>,
    contextItem: ContextItem
  ): RequirementAdditionalData;
  isLoading: boolean;
  dataUpdatedAt: string;
};

type FieldConfigFunction<
  RequirementData extends RequirementTableItemData,
  ContextData extends ContextItem,
  AdditionalData,
  Result
> = (
  dataItem: RequirementData,
  contextItem: ContextData,
  additionalData: AdditionalData
) => Result;

export type CellsConfig<
  R extends RequirementTableItemData,
  C extends ContextItem,
  A
> = {
  isActionRequired: FieldConfigFunction<R, C, A, boolean>;
  requirementName?: FieldConfigFunction<R, C, A, string>;
  requirementDetailsWidgets?: FieldConfigFunction<R, C, A, React.ReactNode>;
  requirementDetailsProgressWidget?: FieldConfigFunction<
    R,
    C,
    A,
    StepItem[] | undefined
  >;
  requirementInstructions?: FieldConfigFunction<
    R,
    C,
    A,
    (instructions: string) => React.ReactNode
  >;
  requirementInstructionsWidgets?: FieldConfigFunction<
    R,
    C,
    A,
    React.ReactNode
  >;
  requirementBasicInformation?: FieldConfigFunction<R, C, A, WSDataItemProps[]>;
  requirementDetails?: FieldConfigFunction<
    R,
    C,
    A,
    DataViewProps | React.ReactNode
  >;
  requirementAdditionalContent?: FieldConfigFunction<R, C, A, React.ReactNode>;
  toolbarActions?: FieldConfigFunction<R, C, A, RequirementAction[]>;
  requirementStatusAlert?: FieldConfigFunction<
    R,
    C,
    A,
    (WSAlertProps & { children?: React.ReactNode }) | undefined
  >;
  nameCell: FieldConfigFunction<R, C, A, TableCellProps>;
  typeCell: FieldConfigFunction<R, C, A, TableCellProps>;
  statusCell: FieldConfigFunction<R, C, A, TableCellProps>;
};

export type RequirementTableRowConfig = {
  [K in RequirementType]: RequirementFieldsConfig<K>;
};

export type RequirementTableCallback = (
  eventName: RequirementEvent,
  item: RequirementTableItemData
) => void;

export type BaseCreateRequirementDefinitionFormValues = {
  name: string;
  type: RequirementType;
  description?: string;
  isApprovalRequired: boolean;
  reviewStrategy?: RequirementReviewStrategy;
};

export type CreateRequirementDefinitionFormValues =
  BaseCreateRequirementDefinitionFormValues &
    CreateRequirementDefinitionSignaturePartialForm &
    CreateRequirementDefinitionSharedFilePartialForm &
    CreateRequirementDefinitionBackgroundChecksPartialForm &
    CreateRequirementDefinitionInsuranceCoveragePartialForm;

export type RequirementStatusViewProps = {
  pill: WSPillProps;
  secondaryText?: string;
};
